import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import type { Folder } from '@/types/database';
import type {
  FolderFormInput,
  FolderTreeNode,
  FolderWithCount,
  BreadcrumbItem,
} from '@/features/sources/types';

const FOLDERS_KEY = 'folders';
const FOLDER_KEY = 'folder';
const FOLDER_TREE_KEY = 'folder-tree';
const BREADCRUMB_KEY = 'breadcrumb';

/** Fetch all folders for the current user as a flat list */
export function useFolders(parentId?: string | null) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [FOLDERS_KEY, user?.id, parentId],
    queryFn: async (): Promise<FolderWithCount[]> => {
      if (!user) return [];

      let query = supabase
        .from('folders')
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true });

      if (parentId === null || parentId === undefined) {
        query = query.is('parent_id', null);
      } else {
        query = query.eq('parent_id', parentId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const folders = (data ?? []) as Folder[];
      if (folders.length === 0) return [];

      // Batch count query: exclude duplicates, single query for all folders
      const folderIds = folders.map((f) => f.id);
      const { data: counts } = await supabase
        .from('folder_sources')
        .select('folder_id, source:sources!inner(is_duplicate)')
        .in('folder_id', folderIds)
        .or('is_duplicate.is.null,is_duplicate.eq.false', { referencedTable: 'sources' });

      const countMap = new Map<string, number>();
      for (const row of counts ?? []) {
        const fid = row.folder_id as string;
        countMap.set(fid, (countMap.get(fid) ?? 0) + 1);
      }

      return folders.map((folder) => ({
        ...folder,
        source_count: countMap.get(folder.id) ?? 0,
      }));
    },
    enabled: !!user,
  });
}

/** Fetch a single folder by ID */
export function useFolder(folderId: string | undefined) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [FOLDER_KEY, folderId],
    queryFn: async (): Promise<FolderWithCount | null> => {
      if (!folderId || !user) return null;

      const { data, error } = await supabase
        .from('folders')
        .select('*')
        .eq('id', folderId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      // Count non-duplicate sources in this folder
      const { data: counts } = await supabase
        .from('folder_sources')
        .select('folder_id, source:sources!inner(is_duplicate)')
        .eq('folder_id', folderId)
        .or('is_duplicate.is.null,is_duplicate.eq.false', { referencedTable: 'sources' });

      return { ...(data as Folder), source_count: counts?.length ?? 0 };
    },
    enabled: !!folderId && !!user,
  });
}

/** Build a full folder tree for the current user */
export function useFolderTree() {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [FOLDER_TREE_KEY, user?.id],
    queryFn: async (): Promise<FolderTreeNode[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('folders')
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true });

      if (error) throw error;

      const folders = (data ?? []) as Folder[];

      const folderIds = folders.map((f) => f.id);
      const { data: counts } = await supabase
        .from('folder_sources')
        .select('folder_id, source:sources!inner(is_duplicate)')
        .in('folder_id', folderIds)
        .or('is_duplicate.is.null,is_duplicate.eq.false', { referencedTable: 'sources' });

      const countMap = new Map<string, number>();
      for (const row of counts ?? []) {
        const fid = row.folder_id as string;
        countMap.set(fid, (countMap.get(fid) ?? 0) + 1);
      }

      const nodeMap = new Map<string, FolderTreeNode>();
      for (const folder of folders) {
        nodeMap.set(folder.id, {
          ...folder,
          children: [],
          source_count: countMap.get(folder.id) ?? 0,
        });
      }

      const roots: FolderTreeNode[] = [];
      for (const node of nodeMap.values()) {
        if (node.parent_id && nodeMap.has(node.parent_id)) {
          nodeMap.get(node.parent_id)!.children.push(node);
        } else {
          roots.push(node);
        }
      }

      return roots;
    },
    enabled: !!user,
  });
}

/** Build breadcrumb path from a folder to root */
export function useBreadcrumb(folderId: string | undefined) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [BREADCRUMB_KEY, folderId],
    queryFn: async (): Promise<BreadcrumbItem[]> => {
      if (!folderId || !user) return [];

      // Single query: fetch all user folders, walk client-side
      const { data, error } = await supabase
        .from('folders')
        .select('id, name, parent_id')
        .eq('user_id', user.id);

      if (error || !data) return [];

      const folderMap = new Map<string, Pick<Folder, 'id' | 'name' | 'parent_id'>>();
      for (const row of data) {
        folderMap.set(row.id, row as Pick<Folder, 'id' | 'name' | 'parent_id'>);
      }

      const path: BreadcrumbItem[] = [];
      const visited = new Set<string>();
      let currentId: string | null = folderId;

      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const folder = folderMap.get(currentId);
        if (!folder) break;
        path.unshift({ id: folder.id, name: folder.name });
        currentId = folder.parent_id;
      }

      return path;
    },
    enabled: !!folderId && !!user,
  });
}

/** Create a new folder */
export function useCreateFolder() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async (input: FolderFormInput): Promise<Folder> => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('folders')
        .insert({
          user_id: user.id,
          name: input.name,
          description: input.description,
          parent_id: input.parent_id,
          folder_type: input.folder_type,
          color: input.color,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Folder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FOLDERS_KEY] });
      queryClient.invalidateQueries({ queryKey: [FOLDER_TREE_KEY] });
      toast.success('Folder created');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create folder');
    },
  });
}

/** Update an existing folder */
export function useUpdateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<FolderFormInput>;
    }): Promise<Folder> => {
      const { data, error } = await supabase
        .from('folders')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Folder;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [FOLDERS_KEY] });
      queryClient.invalidateQueries({ queryKey: [FOLDER_KEY, data.id] });
      queryClient.invalidateQueries({ queryKey: [FOLDER_TREE_KEY] });
      queryClient.invalidateQueries({ queryKey: [BREADCRUMB_KEY] });
      toast.success('Folder updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update folder');
    },
  });
}

/** Delete a folder (cascades to subfolder/folder_sources via DB) */
export function useDeleteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (folderId: string): Promise<void> => {
      const { error } = await supabase
        .from('folders')
        .delete()
        .eq('id', folderId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FOLDERS_KEY] });
      queryClient.invalidateQueries({ queryKey: [FOLDER_TREE_KEY] });
      queryClient.invalidateQueries({ queryKey: [BREADCRUMB_KEY] });
      toast.success('Folder deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete folder');
    },
  });
}

/** Move a folder to a new parent (or root if parentId is null) */
export function useMoveFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      folderId,
      newParentId,
    }: {
      folderId: string;
      newParentId: string | null;
    }): Promise<Folder> => {
      // Guard: can't move into self
      if (newParentId === folderId) {
        throw new Error('Cannot move a folder into itself');
      }

      // Guard: check for circular reference
      if (newParentId) {
        const { data: allFolders } = await supabase
          .from('folders')
          .select('id, parent_id');

        const parentMap = new Map<string, string | null>();
        for (const f of allFolders ?? []) {
          parentMap.set(f.id, f.parent_id);
        }

        const visited = new Set<string>();
        let current: string | null = newParentId;
        while (current && !visited.has(current)) {
          if (current === folderId) {
            throw new Error('Cannot move a folder into one of its descendants');
          }
          visited.add(current);
          current = parentMap.get(current) ?? null;
        }
      }

      const { data, error } = await supabase
        .from('folders')
        .update({
          parent_id: newParentId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', folderId)
        .select()
        .single();

      if (error) throw error;
      return data as Folder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FOLDERS_KEY] });
      queryClient.invalidateQueries({ queryKey: [FOLDER_TREE_KEY] });
      queryClient.invalidateQueries({ queryKey: [BREADCRUMB_KEY] });
      toast.success('Folder moved');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to move folder');
    },
  });
}
